// MACHINERY V2 (Stroje) — profile action set + handlers (M2.4; dialog state
// split into useProfileDialogs.ts at MDEL-FE).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §10 (permission matrix) + data
// package §1 "Actions (+ gates)" + MACHINE_DELETE_SPEC.md §7.5. Reference impl:
// fleet/useFleetHandlers.ts.
//
// >>> THE ACTION SET IS THE FLEET'S. THE GATES ARE THE FLEET'S. <<<
// `buildFleetActions(machine, ctx, handlers)` decides which entries a given user
// sees on a given machine — here exactly as on a card or a row. The gate keeps
// exactly one implementation, and both gate booleans come from the same
// `usePermission("Machinery")` the live profile's PermissionGuard checks, so a
// Worker never sees a Write entry and a Manager never sees the Admin one. `ctx`
// is a NAMED object rather than positional booleans (review finding 3): the old
// signature type-checked identically with canWrite and canAdmin swapped.
//
// THE COLLAPSE IS NOT UNIFORM ACROSS SURFACES. FleetCardMenu.tsx cites THIS
// file as the authority for the profile half, so keep these in step with it —
// a decommissioned machine yields:
//   FLEET   · Admin      : Profil · QR · Obnoviť · Zmazať natrvalo
//   FLEET   · Write user : Profil · QR · Obnoviť
//   FLEET   · Worker     : Profil alone
//   PROFILE · Admin      : QR only — 2. below promotes Obnoviť AND Zmazať
//                          natrvalo into the banner and filters the
//                          self-referential Profil, and reportProblem is
//                          suppressed when decommissioned
//   PROFILE · Write user : QR only (same, minus a delete entry to promote)
//   PROFILE · Worker     : EMPTY — FleetCardMenu renders no kebab at all
// The fleet's list is therefore never empty; only the profile's can be.
//
// WHAT DIFFERS FROM THE FLEET, AND WHY:
//  1. REFRESH TARGET. Every profile mutation refetches THIS machine
//     (`getMachine(id)`), not the fleet lists. Reusing useFleetHandlers verbatim
//     would refetch a grid nobody is looking at and leave the hero rendering
//     pre-edit values — the decommission banner would never appear. Same
//     handlers, surface-correct refresh. The ONE exception is delete: there is
//     no machine left to refetch, so it navigates to the fleet instead.
//  2. FIVE ENTRIES ARE PROMOTED OUT OF THE KEBAB (artboard 0504): Vytvoriť
//     servisnú správu + Vytvoriť žiadosť become the two hero buttons, Obnoviť
//     and Zmazať natrvalo live in the decommission banner (data package §1:
//     Obnoviť "rendered only inside the banner"; delete joins it because that
//     banner IS the decommissioned-machine surface — MACHINE_DELETE_SPEC §7.6),
//     and Profil is self-referential on the profile. They are FILTERED from the
//     menu, never re-gated: the promoted controls render only when
//     buildFleetActions put their entry in the set, so the gate still has
//     exactly one implementation.
//  3. REPORT PROBLEM IS ADDED. It is a profile action in data package §1
//     (Read-level, hidden when decommissioned) that no fleet surface carries,
//     so `buildFleetActions` has no entry for it. Dropping it would be a §1
//     action-parity regression, so it is appended here — additive, never a
//     modification of the shared builder.
//
// Toasts are the sanctioned v2 primitive: handlers dispatch setSuccess/setError
// and MachineryToastHost renders them. It is mounted TWICE on the v2 surface —
// ProfileBody.tsx:199 and fleet/FleetOverlays.tsx:74 — which is what lets the
// delete success toast survive the navigate() away from this page: the state
// lives on the slice, and the fleet mounts its own renderer for it. Restore and
// delete both reuse the EXISTING confirmation dialog, exactly as the fleet does.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import { Machine } from "../../models/models";
import {
  createMachine,
  getMachine,
  restoreMachine,
  setError,
  setSuccess,
} from "../../customerMachinerySlice";
import { useV2Confirm } from "../feedback/useV2Confirm";
import { FleetMenuAction } from "../fleet/FleetCardMenu";
import { FleetActionContext, buildFleetActions } from "../fleet/fleetActions";
import { buildDuplicatePayload } from "../fleet/fleetDuplicate";
import { useMachineDelete } from "../fleet/useMachineDelete";
import { openSrCreate } from "../sr/srNavigation";
import { ProfileDialogsProps } from "./ProfileDialogs";
import { useProfileDialogs } from "./useProfileDialogs";

/** Entries the profile renders as page chrome instead of menu rows (see 2. and
 *  3. in the header). Filtered, NOT re-gated. */
const PROMOTED_OUT_OF_KEBAB = ["profile", "sr", "rfs", "restore", "delete"];

export interface ProfileActions {
  /** Kebab entries for the hero's action stack. */
  actions: FleetMenuAction[];
  /** Null when the gate/state removed the entry — the button then does not render. */
  onCreateServiceReport: (() => void) | null;
  onCreateServiceRequest: (() => void) | null;
  /** Banner-only (data package §1). Null when the user cannot restore. */
  onRestore: (() => void) | null;
  /** Banner-only (MACHINE_DELETE_SPEC §7.6). Null unless the user is an Admin
   *  AND the machine is decommissioned — i.e. unless buildFleetActions produced
   *  the entry. */
  onDelete: (() => void) | null;
  /** True while a delete flow is running — the banner disables its button and
   *  swaps the label, so the user is never left wondering (review finding 2). */
  deleteBusy: boolean;
  dialogProps: Omit<ProfileDialogsProps, "machine">;
}

export const useProfileActions = (machine: Machine): ProfileActions => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirmV2 = useV2Confirm();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const { canWrite, canAdmin } = usePermission(Modules.Machinery);
  const { requestDelete, deleteBusy } = useMachineDelete();

  const machineId = machine.id;

  /** The one refresh every profile mutation ends with. */
  const refresh = useCallback(() => {
    dispatch(getMachine(machineId));
  }, [dispatch, machineId]);

  const dialogs = useProfileDialogs(refresh);

  const handleDuplicate = useCallback(
    async (m: Machine) => {
      // The v2 payload carries catalogModelId AND locationId (fleetDuplicate.ts
      // — the §9 D4 fix). Shared with the fleet so it cannot regress on one
      // surface only.
      const payload = buildDuplicatePayload(m);
      if (!payload) {
        dispatch(setError(translate("myMachinery:v2.fleet.duplicateMissingFields")));
        return;
      }
      // REVIEW (M2.4). The dispatch was NOT unwrapped, so a REJECTED create
      // resolved normally and the success toast fired anyway — stacked under
      // the error toast the slice had just raised. `handleRestore` below is the
      // pattern: unwrap inside a try, toast ONLY on the success path, swallow
      // in catch (the slice already stored the error and MachineryToastHost
      // renders it).
      try {
        await dispatch(createMachine(payload)).unwrap();
        // No refresh: the copy is a DIFFERENT machine and this page shows the
        // original, which did not change. The toast is the only feedback there
        // is — on the fleet the new card appearing is the feedback.
        dispatch(setSuccess(translate("myMachinery:v2.profile.duplicated")));
      } catch {
        // Error surfaced by the slice -> MachineryToastHost.
      }
    },
    [dispatch, translate]
  );

  const handleRestore = useCallback(() => {
    // The EXISTING confirm dialog, reused — §9 D4 carries the action, not a
    // redesign of its dialog. Opened through the v2 gate so that a delete
    // eligibility read finishing a moment later cannot replace THIS dialog in
    // place and turn a click already in motion into a hard delete (review
    // finding 1). Registering is the point, not restore's own risk.
    const opened = confirmV2({
      title: translate("myMachinery:restoreMachine"),
      text: translate("myMachinery:restoreConfirm"),
      confirmButton: translate("myMachinery:restore"),
      // `common.json` has NO top-level `cancel` key — tCommon("cancel")
      // rendered the literal string "cancel" in BOTH locales. The real key is
      // buttons.cancel (en "Cancel" / sk "Zrušiť").
      closeButton: tCommon("buttons.cancel"),
      severity: "neutral",
      // ConfirmationProvider neither awaits nor catches this callback, so an
      // unwrapped rejection would be an unhandled promise rejection AND would
      // skip the refresh (fleet review F6, same fix).
      onConfirm: async () => {
        try {
          await dispatch(restoreMachine(machineId)).unwrap();
          dispatch(setSuccess(translate("myMachinery:restored")));
        } catch {
          // Error surfaced by the slice -> MachineryToastHost.
        }
        refresh();
      },
    });
    // Refused rather than dropped.
    if (!opened) dispatch(setError(translate("myMachinery:v2.delete.confirmBusy")));
  }, [confirmV2, dispatch, machineId, refresh, translate, tCommon]);

  // Hard delete (MDEL-FE). The flow — eligibility read, the confirm that names
  // the machine, the enumerated-blocker toast — is useMachineDelete's on BOTH
  // surfaces; the profile-specific half is only the aftermath. There is no
  // machine left to refetch, so we leave for the fleet instead of calling
  // refresh(), which would 404 on the machine we just removed.
  const handleDelete = useCallback(
    (m: Machine) => {
      requestDelete(m, () => navigate("/machinery-v2"));
    },
    [requestDelete, navigate]
  );

  const fullActions = useMemo(() => {
    // The profile never disables the KEBAB delete row — the entry is filtered
    // out of the menu here and promoted into the banner, which owns the busy
    // affordance. `deleteBusy` is still passed so the context has one shape on
    // both surfaces and a future profile kebab cannot silently lose it.
    const ctx: FleetActionContext = { canWrite, canAdmin, deleteBusy };
    return buildFleetActions(machine, ctx, {
        // Self-referential here; filtered out of the menu below. Kept as a
        // no-op so the handler map stays complete and type-checked.
        onProfile: () => undefined,
        onQrCode: dialogs.openQr,
        // M2.6: the v2 SR create page, opened through srNavigation so the
        // SHARED edit target is cleared first. CARRIED FIX — this call site
        // had only the navigate half, while v1 clears the slot before it
        // (components/ServiceReports.tsx:167-170, personally read in this
        // worktree). Without the clear, "Vytvoriť servisnú správu" after
        // viewing or editing any report opened the form pre-filled as an EDIT
        // of that report, and saving would PUT over it.
        onCreateServiceReport: (m) => openSrCreate(dispatch, navigate, m.id),
        // The live profile carries a returnTo (data package §1); it points at
        // the v2 profile so the round trip stays on the v2 surface.
        onCreateServiceRequest: (m) =>
          navigate(
            `/service-requests/new?machine=${m.id}&returnTo=/machinery-v2/${m.id}`
          ),
        onEditDetails: dialogs.openEdit,
        onDuplicate: handleDuplicate,
        onAssignGroup: dialogs.openAssignGroup,
        onDecommission: dialogs.openDecommission,
        onRestore: handleRestore,
        onDelete: handleDelete,
      });
  }, [
    machine,
    canWrite,
    canAdmin,
    deleteBusy,
    dispatch,
    navigate,
    dialogs,
    handleDuplicate,
    handleRestore,
    handleDelete,
  ]);

  const findAction = useCallback(
    (id: string) => fullActions.find((a) => a.id === id) || null,
    [fullActions]
  );

  const openReportProblem = dialogs.openReportProblem;
  const actions = useMemo(() => {
    const kebab = fullActions.filter(
      (a) => PROMOTED_OUT_OF_KEBAB.indexOf(a.id) < 0
    );
    // Read-level, and hidden on a decommissioned machine — the live profile's
    // exact gate. Suppressing it here is what leaves the PROFILE's
    // decommissioned kebab at QR alone; data package §1's "QR is the sole
    // surviving action" is about THIS surface only. The fleet's kebab still
    // carries Profil, plus Obnoviť for a Write user and the delete row for an
    // Admin.
    if (!machine.isDecommissioned) {
      kebab.push({
        id: "reportProblem",
        labelKey: "myMachinery:reportProblem.button",
        onSelect: openReportProblem,
      });
    }
    return kebab;
  }, [fullActions, machine.isDecommissioned, openReportProblem]);

  const srAction = findAction("sr");
  const rfsAction = findAction("rfs");
  const restoreAction = findAction("restore");
  const deleteAction = findAction("delete");

  return {
    actions,
    onCreateServiceReport: srAction ? srAction.onSelect : null,
    onCreateServiceRequest: rfsAction ? rfsAction.onSelect : null,
    onRestore: restoreAction ? restoreAction.onSelect : null,
    onDelete: deleteAction ? deleteAction.onSelect : null,
    deleteBusy,
    dialogProps: dialogs.dialogProps,
  };
};

export default useProfileActions;
